import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { hasExtension } from '@/hooks/useExtensionUtil';
import { isExtension } from '@/utils/platform';

type HiddenSessionsRouteProps = {
  children: React.ReactNode;
};

/**
 * Route guard for the extension hidden-window shell (`#/sessions`).
 * - Extension: render children even when signed out (no login redirect).
 * - Web: redirect to `/tabs` once extension detection resolves false.
 */
export const HiddenSessionsRoute: React.FC<HiddenSessionsRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const inExtension = isExtension();
  const [webRedirectPending, setWebRedirectPending] = useState(!inExtension);

  useEffect(() => {
    if (inExtension) return;

    let cancelled = false;
    hasExtension().then((ext) => {
      if (cancelled) return;
      if (!ext) {
        navigate('/tabs', { replace: true });
      } else {
        setWebRedirectPending(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [inExtension, navigate]);

  if (inExtension) {
    return <>{children}</>;
  }

  if (webRedirectPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  return <>{children}</>;
};
